import React from 'react';

interface NotificationToastProps {
  message: string | null;
  type: 'error' | 'success';
  onDismiss: () => void;
  /** Optional extra content below the message (e.g., an "Editar" link) */
  children?: React.ReactNode;
}

/**
 * NotificationToast - Reusable toast notification for error/success messages.
 *
 * Renders a fixed-position toast in the top-right corner with slide-in animation.
 * Returns null when there is no message.
 */
export function NotificationToast({ message, type, onDismiss, children }: NotificationToastProps) {
  if (!message) return null;

  const isError = type === 'error';
  const bgClass = isError ? 'bg-red-900/20' : 'bg-green-900/20';
  const borderClass = isError ? 'border-red-500/50' : 'border-green-500/50';
  const textClass = isError ? 'text-red-400' : 'text-green-400';
  const hoverBgClass = isError ? 'hover:bg-red-500/20' : 'hover:bg-green-500/20';

  return (
    <div className="fixed top-4 right-4 z-50 max-w-md animate-in slide-in-from-top-5 fade-in duration-300">
      <div
        className={`${bgClass} border ${borderClass} rounded-lg shadow-lg p-4 flex items-start gap-3`}
      >
        <div className="flex-1">
          <p className={`text-sm font-medium ${textClass}`}>{message}</p>
          {children}
        </div>
        <button
          onClick={onDismiss}
          className={`p-1 ${hoverBgClass} rounded transition-colors flex-shrink-0`}
          aria-label="Cerrar"
        >
          <svg
            className={`w-4 h-4 ${textClass}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
